import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { getSettings, saveSettings } from '@/lib/calendar';
import { type KioskSettings } from '@/types/calendar';
import { ArrowLeft, Save, Download } from 'lucide-react';

interface SettingsPageProps {
  onBack: () => void;
}

export function SettingsPage({ onBack }: SettingsPageProps) {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { isSubmitSuccessful },
  } = useForm<KioskSettings>({
    defaultValues: {
      roomName: '',
      refreshInterval: 60,
      googleCalendarEnabled: false,
      googleResourceEmail: '',
      zoomEnabled: false,
      zoomApiKey: '',
      zoomApiSecret: '',
      zoomUserId: '',
      teamsEnabled: false,
      teamsClientId: '',
      teamsClientSecret: '',
      teamsTenantId: '',
      teamsUserId: '',
    },
  });

  const googleEnabled = watch('googleCalendarEnabled');
  const zoomEnabled = watch('zoomEnabled');
  const teamsEnabled = watch('teamsEnabled');

  useEffect(() => {
    const loadedSettings = getSettings();
    reset(loadedSettings);
  }, [reset]);

  const onSubmit = (data: KioskSettings) => {
    saveSettings(data);
  };

  const downloadKioskScript = () => {
    const link = document.createElement('a');
    link.href = '/kiosk-setup.bat';
    link.download = 'kiosk-setup.bat';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={onBack}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-3xl font-bold text-content-1 font-serif">Kiosk Settings</h1>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* General Settings */}
          <Card>
            <CardHeader>
              <CardTitle>General Settings</CardTitle>
              <CardDescription>Settings that apply to all calendar sources</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="roomName" className="text-sm font-medium">
                  Room Name
                </label>
                <Input
                  id="roomName"
                  type="text"
                  placeholder="Conference Room A"
                  {...register('roomName')}
                />
                <p className="text-xs text-muted-foreground">
                  Display name for this meeting room (shown in the header)
                </p>
              </div>

              <div className="space-y-2">
                <label htmlFor="refreshInterval" className="text-sm font-medium">
                  Refresh Interval (seconds)
                </label>
                <Input
                  id="refreshInterval"
                  type="number"
                  min="10"
                  max="600"
                  {...register('refreshInterval', { valueAsNumber: true })}
                />
                <p className="text-xs text-muted-foreground">
                  How often to refresh the meeting list (10-600 seconds)
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Google Calendar */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Google Calendar</CardTitle>
                  <CardDescription>Display meetings from Google Calendar</CardDescription>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-4 h-4 cursor-pointer"
                    {...register('googleCalendarEnabled')}
                  />
                  <span className="text-sm font-medium">Enable</span>
                </label>
              </div>
            </CardHeader>
            {googleEnabled && (
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="googleResourceEmail" className="text-sm font-medium">
                    Resource Email
                  </label>
                  <Input
                    id="googleResourceEmail"
                    type="email"
                    placeholder="conference-room@resource.calendar.google.com"
                    {...register('googleResourceEmail')}
                  />
                  <p className="text-xs text-muted-foreground">
                    The email address of the Google Calendar resource or meeting room
                  </p>
                </div>
              </CardContent>
            )}
          </Card>

          <div className="p-4 rounded-lg bg-info/10 border border-info/30">
            <p className="text-sm text-content-1">
              <strong>Note:</strong> This kiosk automatically detects and displays Zoom and
              Microsoft Teams meetings from your Google Calendar. Simply include Zoom or Teams
              meeting links in your calendar events.
            </p>
          </div>

          {/* Windows Kiosk Setup */}
          <Card>
            <CardHeader>
              <CardTitle>Windows Kiosk Setup</CardTitle>
              <CardDescription>
                Turn any Windows PC into a dedicated kiosk instantly
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-content-2">
                Download and run this script on a Windows PC to automatically configure it for kiosk
                mode. The PC will restart and launch this application in fullscreen using Microsoft
                Edge.
              </p>
              <div className="flex flex-col gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={downloadKioskScript}
                  className="flex items-center gap-2 w-fit"
                >
                  <Download className="h-4 w-4" />
                  Download Kiosk Setup Script (.bat)
                </Button>
                <p className="text-xs text-muted-foreground">
                  Right-click the downloaded file and select "Run as administrator"
                </p>
              </div>
            </CardContent>
          </Card>

          <div className="flex gap-2 items-center">
            <Button type="submit" className="flex items-center gap-2">
              <Save className="h-4 w-4" />
              Save Settings
            </Button>
            {isSubmitSuccessful && (
              <span className="text-sm text-success flex items-center">
                Settings saved successfully!
              </span>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
